import { DateTime } from "luxon";

export interface ShiftLockState {
  currentShift: {
    date: DateTime;
    dayOrNight: "D" | "N";
  };
  lockAfterDays: number;
  manuallyUnlockedShifts: {
    date: DateTime;
    dayOrNight: "D" | "N";
  }[];
}

function shiftIndex(date: DateTime | null, shift: "D" | "N"): number {
  if (!date) return 0;
  const dayIndex = Math.floor(date.startOf("day").toMillis() / 86400000);
  return dayIndex * 2 + (shift === "N" ? 1 : 0);
}

function isAutoLocked(
  targetDate: DateTime,
  targetShift: "D" | "N",
  current: { date: DateTime; shift: "D" | "N" },
  lockAfterDays: number
): boolean {
  const diffShifts =
    shiftIndex(current.date, current.shift) -
    shiftIndex(targetDate, targetShift);

  return diffShifts >= lockAfterDays * 2;
}

function isFutureShift(
  targetDate: DateTime,
  targetShift: "D" | "N",
  current: { date: DateTime; shift: "D" | "N" }
): boolean {
  return (
    shiftIndex(targetDate, targetShift) >
    shiftIndex(current.date, current.shift)
  );
}

function isManuallyUnlocked(
  date: DateTime,
  shift: "D" | "N",
  unlocked: { date: DateTime; dayOrNight: "D" | "N" }[]
): boolean {
  return unlocked.some(
    s =>
      s.dayOrNight === shift &&
      s.date.hasSame(date, "day")
  );
}

export function isShiftEditable(
  date: DateTime | null,
  shift: "D" | "N",
  state: ShiftLockState | null
): boolean {
  if (!state || !date) return true;

  const current = {
    date: state.currentShift.date,
    shift: state.currentShift.dayOrNight
  };

  if (isFutureShift(date, shift, current)) {
    return false;
  }

  if (isManuallyUnlocked(date, shift, state.manuallyUnlockedShifts)) {
    return true;
  }

  return !isAutoLocked(
    date,
    shift,
    current,
    state.lockAfterDays
  );
}

export function isDayEditable(
  date: DateTime | null,
  state: ShiftLockState | null
): boolean {
  if (!state || !date) return true;

  return (
    isShiftEditable(date, "D", state) ||
    isShiftEditable(date, "N", state)
  );
}

export function isCurrentShift(date: DateTime | null, shift: "D" | "N", state: ShiftLockState | null): boolean {
  if (!state || !date) return false;

  return (
    state.currentShift.date.hasSame(date, "day") &&
    state.currentShift.dayOrNight === shift
  );
}

/**
 * True when the given shift is later than the system's current ("latest") shift.
 * Used to prevent selecting/editing a shift that has not started yet.
 * Defaults to false when the lock state or date is unknown, so the shift stays selectable.
 */
export function isShiftInFuture(
  date: DateTime | null,
  shift: "D" | "N",
  state: ShiftLockState | null
): boolean {
  if (!state || !date) return false;

  return isFutureShift(date, shift, {
    date: state.currentShift.date,
    shift: state.currentShift.dayOrNight,
  });
}